import React, { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Inbox,
  Mail,
  BarChart3,
  Bot,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  Users,
  ListTodo,
  KanbanSquare,
  PieChart,
  Inbox as InboxIcon,
  Keyboard,
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Operations",
    items: [
      { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/admin/contact-requests", label: "Contact Requests", icon: Inbox },
      { to: "/admin/emails", label: "Emails", icon: Mail },
      { to: "/admin/analytics", label: "Visitors & Analytics", icon: BarChart3 },
    ],
  },
  {
    label: "CRM",
    items: [
      { to: "/admin/crm/contacts", label: "Contacts", icon: Users },
      { to: "/admin/crm/pipeline", label: "Pipeline", icon: KanbanSquare },
      { to: "/admin/crm/outreach-inbox", label: "Outreach Inbox", icon: InboxIcon },
      { to: "/admin/crm/tasks", label: "Tasks", icon: ListTodo },
      { to: "/admin/crm/reports", label: "Reports", icon: PieChart },
    ],
  },
  {
    label: "Content & AI",
    items: [
      { to: "/admin/assistant-logs", label: "AI Assistant Logs", icon: Bot },
      { to: "/admin/content", label: "Content Manager", icon: FileText },
      { to: "/admin/blog", label: "Blog Posts", icon: FileText },
      { to: "/admin/assistant", label: "Assistant Test", icon: Bot },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/admin/settings", label: "Settings", icon: Settings },
    ],
  },
];

function NavItem({ to, label, icon: Icon, end, badge, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={`admin-nav-${label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-cyan-600/15 text-cyan-200 ring-1 ring-cyan-500/30"
            : "text-slate-300 hover:text-white hover:bg-slate-800/60"
        }`
      }
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {badge && (
        <span className="text-[10px] uppercase tracking-wide bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const gPressedAt = useRef(0);

  const isTypingTarget = (el) => {
    if (!el) return false;
    const tag = el.tagName;
    return (
      tag === "INPUT" ||
      tag === "TEXTAREA" ||
      tag === "SELECT" ||
      el.isContentEditable
    );
  };

  const handleKey = useCallback((e) => {
    // Esc closes help
    if (e.key === "Escape" && helpOpen) {
      setHelpOpen(false);
      return;
    }

    // Ignore when typing
    if (isTypingTarget(e.target)) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    // "/" focuses search input on the current page if it has one
    if (e.key === "/") {
      const search = document.querySelector('[data-testid="crm-search-input"]');
      if (search) {
        e.preventDefault();
        search.focus();
        search.select?.();
      }
      return;
    }

    // "?" opens help
    if (e.key === "?") {
      e.preventDefault();
      setHelpOpen(true);
      return;
    }

    // "g" prefix
    const now = Date.now();
    if (e.key.toLowerCase() === "g") {
      gPressedAt.current = now;
      return;
    }

    // Within 1.2s of g, jump
    if (now - gPressedAt.current < 1200) {
      const dest = {
        c: "/admin/crm/contacts",
        p: "/admin/crm/pipeline",
        t: "/admin/crm/tasks",
        r: "/admin/crm/reports",
        e: "/admin/crm/outreach-inbox",
        o: "/admin",
        i: "/admin/contact-requests",
        a: "/admin/analytics",
      }[e.key.toLowerCase()];
      if (dest) {
        e.preventDefault();
        navigate(dest);
        toast.success(`→ ${dest.replace("/admin", "Admin")}`, { duration: 800 });
      }
      gPressedAt.current = 0;
    }
  }, [navigate, helpOpen]);

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-slate-900 text-white flex flex-col transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 transition-transform duration-200`}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-bold text-white leading-tight">
                PsyTech Admin
              </div>
              <div className="text-[10px] uppercase tracking-wider text-cyan-400">
                Control Center
              </div>
            </div>
          </div>
          <button
            type="button"
            className="lg:hidden text-slate-400 hover:text-white"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                {group.label}
              </div>
              <div className="space-y-1">
                {group.items.map((n) => (
                  <NavItem key={n.to} {...n} onClick={() => setMobileOpen(false)} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-slate-400">Signed in as</div>
            <div className="text-sm text-white truncate">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-wider text-cyan-400 mt-0.5">
              {user?.role}
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg text-slate-300 hover:bg-red-500/10 hover:text-red-300 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3 flex items-center justify-between">
          <button
            type="button"
            className="lg:hidden text-slate-700"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="text-xs text-slate-500 hidden sm:block">
            <span className="text-slate-700 font-medium">PsyTech</span> · Private admin area
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100"
              title="Keyboard shortcuts (?)"
              data-testid="admin-shortcuts-btn"
            >
              <Keyboard className="w-3.5 h-3.5" />
              <kbd className="hidden sm:inline text-[10px] font-mono">?</kbd>
            </button>
            <span className="hidden sm:inline text-slate-500">
              {new Date().toLocaleDateString(undefined, {
                weekday: "short",
                day: "numeric",
                month: "short",
              })}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-wider font-semibold border border-emerald-200">
              Live
            </span>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* Keyboard shortcuts modal */}
      {helpOpen && <ShortcutsHelp onClose={() => setHelpOpen(false)} />}
    </div>
  );
}

function ShortcutsHelp({ onClose }) {
  const groups = [
    {
      label: "Navigation",
      items: [
        { keys: ["g", "o"], label: "Overview" },
        { keys: ["g", "c"], label: "CRM Contacts" },
        { keys: ["g", "p"], label: "Pipeline" },
        { keys: ["g", "e"], label: "Outreach Inbox" },
        { keys: ["g", "t"], label: "Tasks" },
        { keys: ["g", "r"], label: "Reports" },
        { keys: ["g", "i"], label: "Contact Requests" },
        { keys: ["g", "a"], label: "Analytics" },
      ],
    },
    {
      label: "Actions",
      items: [
        { keys: ["/"], label: "Focus search" },
        { keys: ["?"], label: "Show this help" },
        { keys: ["Esc"], label: "Close dialogs" },
      ],
    },
  ];
  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-shortcuts-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-cyan-700" />
            <h3 className="text-lg font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">Keyboard shortcuts</h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            data-testid="admin-shortcuts-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.label}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">{g.label}</div>
              <ul className="space-y-1.5">
                {g.items.map((it) => (
                  <li key={it.label} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{it.label}</span>
                    <span className="flex items-center gap-1">
                      {it.keys.map((k, i) => (
                        <kbd
                          key={i}
                          className="font-mono text-[11px] bg-slate-100 text-slate-700 ring-1 ring-slate-200 rounded px-1.5 py-0.5 min-w-[24px] text-center"
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-5 leading-relaxed">
          Press <kbd className="font-mono">g</kbd> then a destination key within ~1 second to navigate. Shortcuts pause while typing in inputs.
        </p>
      </div>
    </div>
  );
}
